import { Injectable, inject } from "@angular/core";
import { Observable } from "rxjs";
import { Apollo } from "apollo-angular";
import { map } from "rxjs/operators";
import { GetAttestationsGQL, GetAttestationsQuery, AdherenceUserField, PagingInput, AttestationQueryComplexInput, AttestationSimpleQueryInput, OperatorInput, ComparatorOperators, AdherenceCampaignDto, CloseAdherenceCampaignGQL, CreateOrUpdateAdherenceCampaignGQL, GetAdherenceCampaignsGQL, GetAdherenceCampaignsQuery, CreateOrUpdateAdherenceCampaignMutation, CreateAndSignAttestationMutation, CreateAndSignAttestationGQL, GetAttestationTemplateGQL, CreateOrUpdateAttestationTemplateGQL, GetAttestationTemplateQuery, CreateOrUpdateAttestationTemplateMutation, AttestationTemplateInput, DownloadSelfDashboardGQL, UpdateAttestationGQL, UpdateAttestationMutation } from "../../../graphql/generated";

/**
 * Service for handling Self Dashboard data operations
 * Uses standard dispatch pattern for data fetching
 */
@Injectable({
    providedIn: 'root'
})
export class SelfDashboardService {
    private readonly GetAttestationsGQL: GetAttestationsGQL = inject(GetAttestationsGQL);
    private readonly GetAdherenceCampaignsGQL: GetAdherenceCampaignsGQL = inject(GetAdherenceCampaignsGQL);
    private readonly CreateOrUpdateAdherenceCampaignGQL: CreateOrUpdateAdherenceCampaignGQL = inject(CreateOrUpdateAdherenceCampaignGQL);
    private readonly CloseAdherenceCampaignGQL: CloseAdherenceCampaignGQL = inject(CloseAdherenceCampaignGQL);
    private readonly CreateAndSignAttestationGQL: CreateAndSignAttestationGQL = inject(CreateAndSignAttestationGQL);
    private readonly GetAttestationTemplateGQL: GetAttestationTemplateGQL = inject(GetAttestationTemplateGQL);
    private readonly CreateOrUpdateAttestationTemplateGQL: CreateOrUpdateAttestationTemplateGQL = inject(CreateOrUpdateAttestationTemplateGQL);
    private readonly DownloadSelfDashboardGQL: DownloadSelfDashboardGQL = inject(DownloadSelfDashboardGQL);
    private readonly UpdateAttestationGQL: UpdateAttestationGQL = inject(UpdateAttestationGQL);

    getSelfDashboardDatas(campaignId: string, parameters?: any): Observable<GetAttestationsQuery['getAttestations']> {
        const params = convertLazyLoadToComplex(parameters);
        return this.GetAttestationsGQL.fetch({ campaignId, page: params.page, query: params.query }, { fetchPolicy: 'no-cache' }).pipe(map((res => res.data.getAttestations)))
    }

    updateAttestationNotes(userAccountsId: string, notes: string, campaignId: string): Observable<UpdateAttestationMutation['updateAttestation']> {
        return this.UpdateAttestationGQL.mutate({ userAccountsId, notes, campaignId }).pipe(
            map((result) => result.data?.updateAttestation)
        );
    }

    exportAttestationTemplate(campaignId: string, timeZone: string, parameters?: any) {
        const params = convertLazyLoadToComplex(parameters);
        return this.DownloadSelfDashboardGQL.fetch({ campaignId, query: params.query, timeZone }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.downloadAttestationTemplate))
    }

    // Ottiene tutte le campagne di adherence
    getAdherenceCampaigns(): Observable<GetAdherenceCampaignsQuery['getAdherenceCampaigns']> {
        return this.GetAdherenceCampaignsGQL.fetch({}, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.getAdherenceCampaigns))
    }

    // Crea o aggiorna una campagna di adherence
    createOrUpdateAdherenceCampaign(id?: string, year?: number): Observable<CreateOrUpdateAdherenceCampaignMutation['createOrUpdateAdherenceCampaign']> {
        return this.CreateOrUpdateAdherenceCampaignGQL.mutate({ id, year }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.createOrUpdateAdherenceCampaign))
    }

    // Chiude una campagna di adherence
    closeAdherenceCampaign(id: string): Observable<AdherenceCampaignDto> {
        return this.CloseAdherenceCampaignGQL.mutate({ id }, { fetchPolicy: 'no-cache' })
            .pipe(
                map(result => (result.data?.closeAdherenceCampaign ?? null) as AdherenceCampaignDto)
            );
    }

    createAndSignAttestation(): Observable<CreateAndSignAttestationMutation['createAndSignAttestation']> {
        return this.CreateAndSignAttestationGQL.mutate({}, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.createAndSignAttestation))
    }

    getTemplate(campaignId: string): Observable<GetAttestationTemplateQuery['getAttestationTemplate']> {
        return this.GetAttestationTemplateGQL.fetch({ campaignId }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.getAttestationTemplate))
    }

    saveTemplate(template: AttestationTemplateInput): Observable<CreateOrUpdateAttestationTemplateMutation['createOrUpdateAttestationTemplate']> {
        return this.CreateOrUpdateAttestationTemplateGQL.mutate({ template }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.createOrUpdateAttestationTemplate))
    }

}

export function convertLazyLoadToComplex(parameters: any): { query: AttestationQueryComplexInput, page: PagingInput } {
    const query: AttestationSimpleQueryInput[] = [];

    if (parameters) {

        if (parameters.filters) {

            Object.entries(parameters.filters).forEach(([field, values]) => {
                //console.log"value", values);

                if (values !== null && Array.isArray(values) && values.length > 0) {
                    (ENUM_SELF_DASHBOARD[field as keyof typeof ENUM_SELF_DASHBOARD] || [field]).forEach(key => {
                        const val: string[] = values;
                        query.push({
                            field: key as AdherenceUserField,
                            filters: {
                                operator: ComparatorOperators.Match,
                                value: key === AdherenceUserField.Signed ? val.map(v => v?.toLowerCase() === 'signed' ? 'true' : 'false') : key === AdherenceUserField.LegalEntityName ? val.map(v => v?.split(/-(.+)/)[1]?.trim()) : val.map(v => v?.toLowerCase() === 'empty' ? null : v)
                            }
                        });
                    })
                }
            });
        }
    }
    return {
        query: {
            operator: OperatorInput.And,
            query: [
                {
                    query: query,
                    operator: OperatorInput.And
                }
            ]
        },
        page: {
            pageNo: parameters?.page ?? 0,
            pageSize: parameters?.rows ?? 10
        }
    }
}


export const ENUM_SELF_DASHBOARD = {
    businessUnit: [AdherenceUserField.BusinessUnit],
    organizationalUnit: [AdherenceUserField.OrganizationalUnit],
    legalEntity: [AdherenceUserField.LegalEntityName],
    fullname: [AdherenceUserField.UserAccountsName],
    email: [AdherenceUserField.UserAccountsEmail],
    status: [AdherenceUserField.Status],
    positionHeld: [AdherenceUserField.PositionHeld],
    signed: [AdherenceUserField.Signed],
    timestamp: [AdherenceUserField.SignTimestamp],
    notes: [AdherenceUserField.Notes]

}